import {
    Avatar,
    Field,
    FluentProvider,
    makeStyles,
    mergeClasses,
    TagPicker,
    TagPickerControl,
    TagPickerList,
    TagPickerOption,
    TagPickerOptionGroup,
    tokens,
} from '@fluentui/react-components';
import { memo, useEffect, useRef, useState } from 'react';
import { type Root } from 'react-dom/client';
import { fetchQuery, graphql, RelayEnvironmentProvider, useRelayEnvironment } from 'react-relay';
import { relayEnv } from '../relay/relay-env';
import type {
    TextWithFieldPickerQuery,
    TextWithFieldPickerQuery$data,
} from './__generated__/TextWithFieldPickerQuery.graphql';
import { CustomTagPickerInput } from './TagPickerInput';
import { ThemeProvider, useThemeContext } from '../theme/ThemeContext';

interface IFieldPickerProps {
    getVariables?: () => TPickerOption[];
    label: string;
    list?: string;
    multiline?: boolean;
    numberOfLines?: number;
    onChange: (value: string) => void;
    siteUrl?: string;
    translate: TranslateFn;
    value: string;
}

type TPickerOption = Pick<
    NonNullable<NonNullable<TextWithFieldPickerQuery$data['getSharePointListFields']>[number]>,
    'displayName' | 'fieldType' | 'internalName'
>;

const FieldPicker = memo<IFieldPickerProps>((props) => {
    const [input, setInput] = useState<string>(props.value);
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [options, setOptions] = useState<any[]>(/*() => new Map()*/);
    const [vars, setVars] = useState<any[]>(/*() => new Map()*/);
    const [loading, setLoading] = useState<boolean>(false);
    const loadingRef = useRef(loading);
    const relayEnv = useRelayEnvironment();
    const styles = usePickerStyles();
    const { label, list, siteUrl, translate } = props;
    const disabled = !list || !siteUrl;
    const { theme } = useThemeContext();

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (props.value !== input) {
                props.onChange(input);
            }
        }, 300);
        return () => {
            clearTimeout(timeout);
        };
    }, [input]);

    return (
        <FluentProvider theme={theme} applyStylesToPortals={true} className={styles.provider}>
            <Field
                hint={
                    disabled
                        ? undefined
                        : {
                              className: 'bio-properties-panel-description',
                              children: translate(
                                  loading ? 'Loading...' : 'Press Ctrl to open the selection menu'
                              ),
                          }
                }
                label={{
                    children: label,
                    className: 'bio-properties-panel-label',
                }}
            >
                <TagPicker
                    disabled={disabled}
                    noPopover={disabled}
                    onOpenChange={(_event, data) => {
                        if (!data.open) {
                            setOpen(false);
                            setVars(undefined);
                        } else if (data.open && data.type === 'keydown' && data.event.ctrlKey) {
                            if (!options && !loadingRef.current) {
                                setLoading(true);
                                loadingRef.current = true;
                                fetchQuery<TextWithFieldPickerQuery>(
                                    relayEnv,
                                    query,
                                    {
                                        list: list!,
                                        siteUrl: siteUrl!,
                                    },
                                    { fetchPolicy: 'store-or-network' }
                                )
                                    .toPromise()
                                    .then((queryResult) => {
                                        const fields =
                                            queryResult?.getSharePointListFields || []; /*.filter(
                                        (entry) => entry.peopleField
                                    ) || [];*/
                                        const expandedFields = [];
                                        for (const field of fields) {
                                            expandedFields.push(field);
                                            if (field.fieldType === 20 /*&& field.peopleField*/) {
                                                expandedFields.push({
                                                    displayName: `${field.displayName}.Id`,
                                                    internalName: `${field.internalName}.Id`,
                                                    fieldType: field.fieldType,
                                                });
                                                expandedFields.push({
                                                    displayName: `${field.displayName}.Title`,
                                                    internalName: `${field.internalName}.Title`,
                                                    fieldType: field.fieldType,
                                                });
                                                expandedFields.push({
                                                    displayName: `${field.displayName}.EMail`,
                                                    internalName: `${field.internalName}.EMail`,
                                                    fieldType: field.fieldType,
                                                });
                                                expandedFields.push({
                                                    displayName: `${field.displayName}.Name`,
                                                    internalName: `${field.internalName}.Name`,
                                                    fieldType: field.fieldType,
                                                });
                                            }
                                        }
                                        setOptions(
                                            Array.from(
                                                new Map(
                                                    expandedFields.map((entry) => [
                                                        entry.internalName,
                                                        entry,
                                                    ])
                                                ).values()
                                            )
                                                .sort((a, b) =>
                                                    a.displayName.localeCompare(b.displayName)
                                                )
                                                .map((entry) =>
                                                    createTagPickerOption(entry, styles)
                                                )
                                        );
                                        loadingRef.current = false;
                                        setLoading(false);
                                    })
                                    .catch(() => {
                                        loadingRef.current = false;
                                        setLoading(false);
                                    });
                            }
                            if (props.getVariables) {
                                setVars(
                                    props
                                        .getVariables()
                                        .map((entry) => createTagPickerOption(entry, styles))
                                );
                            }
                            setOpen(true);
                        }
                    }}
                    onOptionSelect={(_event, data) => {
                        if (data.value === 'no-matches') {
                            return;
                        }

                        const iptEl = inputRef.current!;
                        const { value } = iptEl;
                        let pos = iptEl.selectionStart!;
                        const placeholder = `{{${data.value}}}`;
                        setInput(
                            [
                                value.slice(0, pos),
                                placeholder,
                                value.slice(iptEl.selectionEnd!),
                            ].join('')
                        );
                        setTimeout(() => {
                            iptEl.selectionStart = pos + placeholder.length;
                            iptEl.selectionEnd = iptEl.selectionStart;
                        }, 25);
                    }}
                    open={disabled ? false : open}
                    selectedOptions={[]}
                >
                    <TagPickerControl
                        className={mergeClasses(styles.pickerCtrl, 'bio-properties-panel-input')}
                    >
                        <CustomTagPickerInput
                            aria-label={translate('Select {item}', { item: label })}
                            // @ts-ignore
                            as={props.multiline ? 'textarea' : 'input'}
                            className={styles.pickerInput}
                            value={input}
                            onChange={(event: any) => {
                                setInput(event.target.value);
                            }}
                            ref={inputRef}
                            rows={
                                props.multiline
                                    ? props.numberOfLines
                                        ? props.numberOfLines
                                        : 5
                                    : undefined
                            }
                        />
                    </TagPickerControl>

                    <TagPickerList>
                        <TagPickerOptionGroup label={translate('List fields')}>
                            {options && options.length > 0
                                ? options
                                : createTagPickerNoMatchOption(
                                      translate(loading ? 'Loading...' : 'No selection available')
                                  )}
                        </TagPickerOptionGroup>

                        <TagPickerOptionGroup label={translate('Variables')}>
                            {open &&
                                (vars && vars.length > 0
                                    ? vars
                                    : createTagPickerNoMatchOption(
                                          translate('No selection available')
                                      ))}
                        </TagPickerOptionGroup>
                    </TagPickerList>
                </TagPicker>
            </Field>
        </FluentProvider>
    );
});

const usePickerStyles = makeStyles({
    provider: { backgroundColor: 'transparent' },
    pickerCtrl: {
        minHeight: 'unset',
        minWidth: 'unset',
        '::after': {
            display: 'none',
        },
        '&:hover': {
            'border-color': 'var(--input-border-color)',
        },
        '>span': {
            display: 'none',
            minHeight: 'unset',
            right: '4px',
        },
        /*'& .fui-TagPickerControl__expandIcon': {
            minHeight: '28px'
        }*/
    },
    pickerGroup: {
        padding: '4px 0',
        // paddingRight: '15px',
        '>button': {
            backgroundColor: tokens.colorNeutralBackground1,
            fontSize: 'smaller !important',
            height: 'unset',
            paddingLeft: '3px',
        },
    },
    pickerInput: {
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        padding: 'unset',
        resize: 'vertical',
    },
    tagPrimaryText: {
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
    },
});

const query = graphql`
    query TextWithFieldPickerQuery($siteUrl: String!, $list: String!) {
        getSharePointListFields(siteUrl: $siteUrl, listId: $list) {
            displayName
            fieldType
            internalName
            # peopleField
        }
    }
`;

const createTagPickerOption = (
    { displayName, internalName }: TPickerOption,
    styles: ReturnType<typeof usePickerStyles>
) => (
    <TagPickerOption
        key={internalName}
        media={<Avatar shape="square" aria-hidden name={displayName} color="colorful" />}
        text={displayName}
        value={internalName}
    >
        <div className={styles.tagPrimaryText}>{displayName}</div>
    </TagPickerOption>
);

const createTagPickerNoMatchOption = (text: string) => (
    <TagPickerOption value="no-matches">{text}</TagPickerOption>
);

export const renderTextWithFieldPicker = (root: Root, props: IFieldPickerProps) => {
    root.render(
        <RelayEnvironmentProvider environment={relayEnv}>
            <ThemeProvider>
                <FieldPicker {...props} />
            </ThemeProvider>
        </RelayEnvironmentProvider>
    );
};
